import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import notificationSlice from '@/redux/slices/notificationSlice';
import scheduleStateSlice from '@/apps/dispatcher/redux/slices/scheduleSlice';
import workItemStateSlice from '@/apps/dispatcher/redux/slices/workItemSlice';
import workItemSortingSlice from '@/apps/dispatcher/redux/slices/workItemSortingSlice';
import journeySortingSlice from '@/apps/dispatcher/redux/slices/journeySortingSlice';
import filterSlice from '@/apps/dispatcher/redux/slices/filterSlice';
import queuedHistorySlice from '@/apps/dispatcher/redux/slices/queuedHistorySlice';
import statusItemsSlice from '@/apps/health_check/redux/statusItemsSlice';
import statusItemsFiltersSlice from '@/apps/health_check/redux/statusItemsFiltersSlice';
import statusItemsSortingSlice from '@/apps/health_check/redux/statusItemsSortingSlice';
import appSettingsSlice from '@/apps/common/settings/redux/appSettingsSlice';
import configurationSlice from '@/apps/common/settings/redux/configurationSlice';
import differenceCheckerSlice from '@/apps/difference_checker/redux/slices/differenceCheckerSlice';
import differenceCheckerFiltersSlice from '@/apps/difference_checker/redux/slices/differenceCheckerFiltersSlice';
import differenceCheckerSortingSlice from '@/apps/difference_checker/redux/slices/differenceCheckerSortingSlice';
import washingSlice from '@/apps/washing/redux/slices/washingSlice';
import washingHistorySlice from '@/apps/washing/redux/slices/washingHistorySlice';
import { washingApi } from '@/apps/washing/redux/washingApi';
import { statusItemsApi } from '@/apps/health_check/redux/statusItemsApi';
import { delayApi } from '@/apps/common/settings/redux/delayApi';
import minorDelayApi from '@/apps/common/settings/redux/minorDelayApi';
import { projectedDelayApi } from '@/apps/common/settings/redux/projectedDelayApi';
import alarmApi from '@/apps/common/settings/redux/alarmApi';
import healthCheckConfigurationsApi from '@/apps/common/settings/redux/healthCheckConfigurationsApi';
import faultAlarmConfigurationsApi from '@/apps/common/settings/redux/faultAlarmConfigurationsApi';
import { busIsLateConfigurationsApi } from '@/apps/common/settings/redux/busIsLateConfigurationsApi';
import { washRuleConfigurationsApi } from '@/apps/common/settings/redux/washRuleConfigurationsApi';
import { reasonCommentsApi } from '@/apps/common/settings/redux/reasonCommentsApi';
import { comparisonApi } from '@/apps/difference_checker/redux/comparisonApi';
import { reasonCodesApi } from '@/apps/dispatcher/redux/reasonCodesApi';
import { ptaReportsApi } from '@/apps/dispatcher/redux/ptaReportsApi';
import { fieldRequirementsApi } from '@/apps/dispatcher/redux/fieldRequirementsApi';
import { usersApi } from '@/apps/common/user_management/redux/usersApi';
import { washMonitoredDepotsListener } from '@/redux/listeners/washMonitoredDepotsListener';

const store = configureStore({
  reducer: {
    appSettings: appSettingsSlice,
    workItems: workItemStateSlice,
    scheduleItems: scheduleStateSlice,
    notifications: notificationSlice,
    filters: filterSlice,
    queuedHistory: queuedHistorySlice,
    configurations: configurationSlice,
    differenceChecker: differenceCheckerSlice,
    differenceCheckerFilters: differenceCheckerFiltersSlice,
    differenceCheckerSorting: differenceCheckerSortingSlice,
    statusItems: statusItemsSlice,
    statusItemsFilters: statusItemsFiltersSlice,
    statusItemsSorting: statusItemsSortingSlice,
    workItemSorting: workItemSortingSlice,
    journeySorting: journeySortingSlice,
    washing: washingSlice,
    washingHistory: washingHistorySlice,
    [statusItemsApi.reducerPath]: statusItemsApi.reducer,
    [minorDelayApi.reducerPath]: minorDelayApi.reducer,
    [delayApi.reducerPath]: delayApi.reducer,
    [projectedDelayApi.reducerPath]: projectedDelayApi.reducer,
    [busIsLateConfigurationsApi.reducerPath]:
      busIsLateConfigurationsApi.reducer,
    [alarmApi.reducerPath]: alarmApi.reducer,
    [healthCheckConfigurationsApi.reducerPath]:
      healthCheckConfigurationsApi.reducer,
    [faultAlarmConfigurationsApi.reducerPath]:
      faultAlarmConfigurationsApi.reducer,
    [reasonCommentsApi.reducerPath]: reasonCommentsApi.reducer,
    [comparisonApi.reducerPath]: comparisonApi.reducer,
    [reasonCodesApi.reducerPath]: reasonCodesApi.reducer,
    [ptaReportsApi.reducerPath]: ptaReportsApi.reducer,
    [fieldRequirementsApi.reducerPath]: fieldRequirementsApi.reducer,
    [washingApi.reducerPath]: washingApi.reducer,
    [washRuleConfigurationsApi.reducerPath]: washRuleConfigurationsApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      // Prepended, as listener middleware must be, so its effects see actions
      // before the api middlewares process them.
      .prepend(washMonitoredDepotsListener.middleware)
      .concat(statusItemsApi.middleware)
      .concat(minorDelayApi.middleware)
      .concat(delayApi.middleware)
      .concat(projectedDelayApi.middleware)
      .concat(busIsLateConfigurationsApi.middleware)
      .concat(alarmApi.middleware)
      .concat(healthCheckConfigurationsApi.middleware)
      .concat(faultAlarmConfigurationsApi.middleware)
      .concat(reasonCommentsApi.middleware)
      .concat(comparisonApi.middleware)
      .concat(reasonCodesApi.middleware)
      .concat(ptaReportsApi.middleware)
      .concat(fieldRequirementsApi.middleware)
      .concat(washingApi.middleware)
      .concat(washRuleConfigurationsApi.middleware)
      .concat(usersApi.middleware),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;

export default store;
